import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "context/AuthProvider";
import logo from "assets/logo/logo.png";
import Tournament from "Components/Tournament/Tournament";
import LocationDropdown from "Components/Navbar/LocationDropdown";
import {
  fetchArchivedTournamentsByLocation,
  fetchTournamentsByLocation,
  canManage,
} from "services/tournamentService";
import UploadRankingsButton from "Components/Tournament/UploadRankingsButton";
import PlayerRankings from "Components/Tournament/PlayerRankings";
import { fetchLocationById } from "services/locationService";
import LocationRankingUpdater from "Components/Admin/LocationRankingUpdater";
import ExportLocationRankings from "Components/Tournament/ExportLocationRankings";
import TournamentArchive from "Components/Tournament/tournamentArchive";
import { fetchUsers } from "services/userService";
import { getLocationRankings } from "services/rankingService";
import DisplayLocationRankingToggle from "Components/Location/DisplayLocationRankingToggle";
import * as Sentry from "@sentry/react";

const Location = () => {
  const { locationId } = useParams();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [location, setLocation] = useState(null);
  const [users, setUsers] = useState([]);
  const [locationRankings, setLocationRankings] = useState([]);
  const [latestTournamentWithPodium, setLatestTournamentWithPodium] =
    useState(null);
  const [animatePodium, setAnimatePodium] = useState(false);
  const isAdmin = canManage(user, locationId);

  // const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!user) return; // ⬅️ don't even try to load if not authenticated
  
    const loadData = async () => {
      try {
        const promises = [
          fetchLocationById(locationId),
          fetchTournamentsByLocation(locationId),
          fetchArchivedTournamentsByLocation(locationId),
          getLocationRankings(locationId),
        ];
        
        // this has to be fixed, users need to be fetched when there is an Open
        if (isAdmin) {
          promises.push(fetchUsers());
        }
  
        const [
          fetchedLocation,
          fetchedTournaments,
          fetchedArchivedTournaments,
          fetchedRankings,
          fetchedUsers = [],
        ] = await Promise.all(promises);
  
        setLocation(fetchedLocation);
        setTournaments(fetchedTournaments);
        setUsers(fetchedUsers);
        setLocationRankings(fetchedRankings);
  
        const tournamentWithPodiums = fetchedArchivedTournaments
          .filter((t) => t.podiums && t.podiums.length > 0)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  
        setLatestTournamentWithPodium(tournamentWithPodiums || null);
        setTimeout(() => setAnimatePodium(true), 100);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching data:", error);
      }
    };
  
    loadData();
  }, [locationId, isAdmin, user]);

  return (
    <div className="min-h-screen bg-gray-300 flex flex-col items-center text-white w-full">
      {/* Navigation */}
      <nav className="flex flex-col top-0 left-0 w-full bg-gray-800 shadow-xl z-50 pt-3">
        <div className="container mx-auto flex flex-row justify-between items-center px-6 gap-4">
          <Link
            to="/"
            className="flex items-center space-x-3 text-white px-2 py-1 md:px-6 md:py-3 transition"
          >
            <img src={logo} alt="Logo" className="h-8 md:h-12" />
            <span className="text-xl font-semibold hidden md:inline">
              Retour à l'accueil
            </span>
          </Link>

          <div className="flex flex-row">
            {user && <LocationDropdown buttonText="Autres hebdos" />}
            {isAdmin && (
              <Link
                to={`/locations/tournaments/new/${locationId}`}
                className="ml-2 px-3 py-2 bg-green-600 text-sm text-white rounded-sm font-bold hover:bg-green-700 transition"
              >
                <span className="inline md:hidden">+</span>
                <span className="hidden md:inline">+ Nouveau tournoi</span>
              </Link>
            )}
          </div>
        </div>

        <span className="pt-4 pb-2 text-2xl md:text-3xl font-semibold italic text-gray-200 shadow-xl bg-gray-800 w-full text-center">
          {location ? `${location.name} - ${location.town}` : "Chargement ..."}
        </span>
      </nav>

      {/* Podium Display */}
      {latestTournamentWithPodium && (
        <div
          className={`
            flex flex-col pb-4 shadow-xl mt-1 items-center text-white text-sm md:text-base 
             px-6 bg-gray-100 m-2
            transition-all duration-1000 ease-out transform
            ${
              animatePodium
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-20"
            }
          `}
        >
          <span className="text-lg font-semibold mb-1 text-gray-500">
            Résultats du {latestTournamentWithPodium.type}{" "}
            {new Date(latestTournamentWithPodium.date).toLocaleDateString(
              "fr-FR",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            )}
          </span>
          <div className="flex flex-wrap md:flex-row gap-8 text-center justify-center">
            {latestTournamentWithPodium.podiums.map((podium, index) => (
              <div key={index}>
                <span className="font-semibold text-amber-600 block">
                  {podium.name || ""}
                </span>
                <ul className="list-none space-y-1 flex flex-col items-start text-sm md:text-lg text-gray-900">
                  {podium.first && <li>🥇 {podium.first}</li>}
                  {podium.second && <li>🥈 {podium.second}</li>}
                  {podium.third && <li>🥉 {podium.third}</li>}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tournaments */}
      <div className="w-full items-center flex flex-col mb-2">
        {!user ? (
          <div className="text-center p-4">
            <p className="text-xl text-gray-600 mb-4">
              Vous devez être connecté pour voir et vous inscrire aux tournois.
            </p>
            <Link
              to="/login"
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold text-lg rounded-lg transition"
            >
              Se connecter
            </Link>
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center p-4">
            <p className="text-xl text-gray-500">
              Aucun tournoi trouvé pour cet endroit.
            </p>
          </div>
        ) : (
          tournaments
            .filter((t) => t.active === true)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map((tournament) => (
              <div
                key={tournament.id}
                id={`tournament-${tournament.id}`}
                className="w-full flex flex-col items-center p-2 mb-2"
              >
                <Tournament
                  tournament={tournament}
                  user={user}
                  users={users}
                  locationRankings={locationRankings}
                />
              </div>
            ))
        )}

        {/* Admin Tools */}
        {(location?.displayRanking || isAdmin) && (
          <>
            <div className="w-full flex flex-col items-center bg-gray-400 pb-8 shadow-xl border-t border-gray-500">
              <PlayerRankings
                locationRankings={locationRankings}
                isAdmin={isAdmin}
              />
              {/* Admin Tools */}
              {isAdmin && (
                <>
                  <DisplayLocationRankingToggle locationId={locationId} />
                  <div className="hidden md:flex md:flex-row justify-start gap-2 pt-2">
                    <UploadRankingsButton locationId={locationId} />
                    <ExportLocationRankings locationId={locationId} />
                  </div>

                  <LocationRankingUpdater
                    locationId={locationId}
                    locationRankings={locationRankings}
                    users={users}
                  />
                </>
              )}
            </div>
          </>
        )}

        {user && <TournamentArchive locationId={locationId} isAdmin={isAdmin} />}
      </div>
    </div>
  );
};

export default Location;
